import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getStockLevels, getBranches, getProducts, adjustStock } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function AdjustStockModal({ branches, products, onClose, onSaved }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("inventory");
  const { t: tCommon } = useTranslation("common");

  const MOVEMENT_TYPE_OPTIONS = [
    { value: "purchase", label: t("movement_type_purchase", "Purchase (stock in)") },
    { value: "adjustment", label: t("movement_type_adjustment", "Adjustment (correction)") },
    { value: "return", label: t("movement_type_return", "Return (stock in)") },
    { value: "transfer_in", label: t("movement_type_transfer_in", "Transfer in") },
    { value: "transfer_out", label: t("movement_type_transfer_out", "Transfer out") },
  ];

  const [form, setForm] = useState({ branch_id: "", product_id: "", type: "purchase", quantity: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));
  const productOptions = products.map((p) => ({ value: String(p.id), label: `${p.name} (${p.sku})` }));

  const isOutbound = form.type === "transfer_out";

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.branch_id) newErrors.branch_id = t("validation_branch_required", "Branch is required");
    if (!form.product_id) newErrors.product_id = t("validation_product_required", "Product is required");
    const qty = Number(form.quantity);
    if (!form.quantity || qty === 0) newErrors.quantity = t("validation_quantity_nonzero", "Enter a non-zero quantity");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    // The backend stores quantity as a signed delta. "Transfer out" is the
    // only type in this list that always removes stock — every other type
    // here (purchase/adjustment/return/transfer in) adds it. A plain
    // "Adjustment" that needs to remove stock can still be entered as a
    // negative number directly.
    const signedQty = isOutbound ? -Math.abs(qty) : qty;

    setLoading(true);
    try {
      await adjustStock(token, {
        branch_id: Number(form.branch_id),
        product_id: Number(form.product_id),
        type: form.type,
        quantity: signedQty,
        reason: form.reason.trim(),
      });
      notify.success(t("notify_adjusted", "Stock adjusted successfully"));
      onSaved();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_adjust_failed", "Failed to adjust stock"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl">
        <h3 className="text-xl font-black text-gray-900 mb-1">{t("modal_title", "Adjust Stock")}</h3>
        <p className="text-gray-400 text-sm mb-6">{t("modal_subtitle", "Every change is recorded as a stock movement")}</p>

        <div className="space-y-5">
          <div>
            <SelectBox label={t("field_branch_label", "Branch")} options={branchOptions} value={form.branch_id} onChange={(val) => setForm((p) => ({ ...p, branch_id: val }))} />
            {errors.branch_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.branch_id}</p>}
          </div>

          <div>
            <SelectBox label={t("field_product_label", "Product")} options={productOptions} value={form.product_id} onChange={(val) => setForm((p) => ({ ...p, product_id: val }))} />
            {errors.product_id && <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">{errors.product_id}</p>}
          </div>

          <SelectBox label={t("field_movement_type_label", "Movement Type")} options={MOVEMENT_TYPE_OPTIONS} value={form.type} onChange={(val) => setForm((p) => ({ ...p, type: val }))} />

          <FloatingInput
            label={isOutbound ? t("field_quantity_remove_label", "Quantity (will be removed)") : t("field_quantity_add_label", "Quantity (will be added)")}
            type="number"
            value={form.quantity}
            onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
            error={errors.quantity}
          />

          <FloatingInput label={t("field_reason_label", "Reason (optional)")} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />

          <div className="flex gap-3 pt-2">
            <Button variant="primary" onClick={handleSubmit} loading={loading} className="flex-1">{tCommon("btn_save", "Save")}</Button>
            <Button variant="secondary" onClick={onClose} disabled={loading} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Inventory() {
  const { t } = useTranslation("inventory");
  const [levels, setLevels] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [showAdjust, setShowAdjust] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchLevels = async () => {
    try {
      const data = await getStockLevels(token, branchFilter ? { branch_id: branchFilter } : undefined);
      setLevels(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchLevels(); }, [branchFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    getBranches(token).then(setBranches).catch(() => {});
    getProducts(token).then(setProducts).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...levels].filter((l) =>
      l.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.product?.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [levels, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    {
      key: "product",
      label: t("col_product", "Product"),
      render: (row) => (
        <div>
          <div className="font-bold">{row.product?.name || `#${row.product_id}`}</div>
          <div className="text-[10px] text-gray-400 font-mono">{row.product?.sku}</div>
        </div>
      ),
    },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || `#${row.branch_id}` },
    {
      key: "quantity",
      label: t("col_on_hand", "On Hand"),
      render: (row) => {
        // Reorder point is a single PRODUCT-LEVEL setting (see the
        // "Reorder Point" field on the product's own edit screen and
        // Product.ReorderPoint) — not per-branch, so it's read off
        // row.product here rather than this StockLevel row itself.
        const reorderPoint = row.product?.reorder_point || 0;
        const low = reorderPoint > 0 && row.quantity <= reorderPoint;
        return (
          <span className={`font-mono font-bold ${low ? "text-red-500" : "text-gray-800"}`}>
            {row.quantity} {row.product?.unit?.symbol || ""}
            {low && <span className="ms-2 text-[10px] uppercase font-bold bg-red-50 text-red-500 px-2 py-0.5 rounded-full">{t("badge_low", "Low")}</span>}
          </span>
        );
      },
    },
    {
      key: "reorder_point",
      label: t("col_reorder_point", "Reorder Point"),
      className: "text-gray-500 font-mono",
      render: (row) => row.product?.reorder_point || "—",
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      {showAdjust && (
        <AdjustStockModal
          branches={branches}
          products={products}
          onClose={() => setShowAdjust(false)}
          onSaved={() => { setShowAdjust(false); fetchLevels(); }}
        />
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Inventory size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Inventory")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Current stock per branch — every change is audited")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput value={searchTerm} onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }} placeholder={t("search_placeholder", "Search products...")} />
          <Button variant="outline" icon={ICONS.StockMovements} onClick={() => navigate("/stock-movements")} className="w-full sm:w-auto shrink-0">
            {t("btn_history", "History")}
          </Button>
          <Button variant="primary" icon={ICONS.Plus} onClick={() => setShowAdjust(true)} className="w-full sm:w-auto shrink-0">
            {t("btn_adjust_stock", "Adjust Stock")}
          </Button>
        </div>
      </PageHeader>

      <div className="max-w-xs mb-4">
        <SelectBox label={t("filter_branch_label", "Filter by Branch")} options={branchOptions} value={branchFilter} onChange={(val) => { setBranchFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row) => (
          <Button
            variant="outline"
            size="sm"
            icon={ICONS.StockMovements}
            onClick={() => navigate(`/stock-movements?product_id=${row.product_id}&branch_id=${row.branch_id}`)}
          >
            {t("btn_history", "History")}
          </Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.branch?.name}</span>
            <h3 className="font-bold text-gray-800">{row.product?.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
